import { MutableRefObject, useCallback, useEffect, useRef } from 'react'

import * as c from './constants'
import { useSideNavigationGlobalState } from './use-side-navigation-global-state'
import styles from './viewport-observer.module.scss';


type Props = {
  containerRef: MutableRefObject<HTMLDivElement | null>
}

export const ViewportObserver = ({ containerRef }: Props) => {
  const viewportRef = useRef<HTMLDivElement | null>(null)

  const { setCapacity, setNavigationHeight } = useSideNavigationGlobalState()

  const onResize = useCallback((entries: Array<ResizeObserverEntry>) => {
    // высота области просмотра браузера
    const viewportHeight = entries[0]?.contentRect?.height
    // Нужно вычислить доступную область (высоту) для бокового меню и урезать его в соответствии с ней.
    // Новый размер будет выравнен по высоте отдельного элемента меню, поэтому сохраняем количество этих элементов
    // (которое вмещается в доступную область) в состояние, и используем в RestrictByCapacity
    // чтоб отобразить только нужное количество. Overflow: hidden нельзя использовать, так как есть
    // выступающие области за границу бокового меню при наведении на пункты (в свернутом состоянии)
    //
    // Доступная высота вычисляется так: высота вьюпорта минус top-позиция меню и минус высота подвала
    const container = containerRef.current
    if (!container) {
      console.error('SideNavigation: Не найден container')
      return
    }

    const sideNavigation = container.getElementsByClassName(c.NAVIGATION_HIDDEN_CLASS_NAME)[0]
    if (!sideNavigation) {
      console.error('SideNavigation: Не найден sideNavigation')
      return
    }
    const { top: sideNavigationTop } = sideNavigation.getBoundingClientRect()
    const sideNavigationHeight = sideNavigation.scrollHeight

    const sideNavigationItem = container.getElementsByClassName(c.ITEM_CLASS_NAME)[0]
    if (!sideNavigationItem) {
      console.error('SideNavigation: Не найден sideNavigationItem')
      return
    }
    const { height: sideNavigationItemHeight } = sideNavigationItem.getBoundingClientRect()

    const sideNavigationToggle = container.getElementsByClassName(c.TOGGLE_BUTTON_CLASS_NAME)[0]
    const sideNavigationToggleHeight = sideNavigationToggle?.getBoundingClientRect()?.height || 0

    const footer = document.getElementById('footer')
    if (!footer) {
      console.error('SideNavigation: Не найден footer')
      return
    }
    const { height: footerHeight } = footer.getBoundingClientRect()

    const availableSpace = viewportHeight - footerHeight - sideNavigationTop

    // не ограничивать и вернуть к сохраненному состоянию, если вписывается
    if (sideNavigationHeight < availableSpace) {
      setCapacity(Infinity)
      return
    }

    const availableSpaceForItems = availableSpace - sideNavigationToggleHeight

    // вычисление высоты для бокового меню, вписывающейся в доступную область и выравненной по высоте отдельного элемента
    const itemsHeightStepped = availableSpaceForItems - (availableSpaceForItems % sideNavigationItemHeight)
    setNavigationHeight(itemsHeightStepped + sideNavigationToggleHeight)

    // сохранение количества вместившихся целых элементов, далее это используется в RestrictByCapacity
    setCapacity(itemsHeightStepped / sideNavigationItemHeight)
  }, [containerRef, setCapacity, setNavigationHeight])

  useEffect(() => {
    const container = viewportRef.current
    const ro = new ResizeObserver(onResize)
    if (container) {
      ro.observe(container)
    }
    return () => {
      if (ro && container) {
        ro.unobserve(container)
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className={styles.root} ref={viewportRef} />
  )
}
